import createOpenApiClient, { type Middleware } from "openapi-fetch";

import { createAuthFetch, describeDevice } from "./auth-fetch";
import type { paths } from "./generated/schema";
import { createLocalStorageTokenStore, type TokenStore } from "./token-store";

/** HTTP methods the API treats as writes, and which therefore need an idempotency key. */
const WRITE_METHODS = new Set(["POST", "PUT", "PATCH", "DELETE"]);

const IDEMPOTENCY_HEADER = "Idempotency-Key";

export interface BreeNovaClientOptions {
  /** Base URL of the API, e.g. `http://localhost:5180`. Empty to use the dev proxy. */
  baseUrl: string;

  /** Where tokens live. Defaults to localStorage. */
  store?: TokenStore;

  /**
   * Called when a 401 survives one silent refresh — the session is over and the shell
   * should send the breeder to sign in.
   */
  onSessionLost?: () => void;

  /**
   * Supplies the idempotency key for a write. Override it to make a key survive an app
   * restart — a key held only in memory is regenerated on relaunch, which is exactly
   * when a breeder retries the write that failed as the app died.
   */
  generateIdempotencyKey?: () => string;
}

/**
 * Hard rule: every write endpoint honours an `Idempotency-Key`, so every write this
 * client sends carries one. Attaching it here rather than at each call site means a new
 * feature cannot forget — and a caller that wants a stable key across retries can still
 * set the header explicitly.
 */
function idempotencyMiddleware(generateKey: () => string): Middleware {
  return {
    onRequest({ request }) {
      if (!WRITE_METHODS.has(request.method.toUpperCase())) {
        return undefined;
      }

      if (!request.headers.has(IDEMPOTENCY_HEADER)) {
        request.headers.set(IDEMPOTENCY_HEADER, generateKey());
      }

      return request;
    },
  };
}

/**
 * The typed API client. Every request and response shape comes from
 * `./generated/schema`, which is generated from the API's own OpenAPI document —
 * hand-writing one of these types is how the client and server quietly diverge.
 *
 * Authentication is handled by the fetch layer underneath: the access token is attached
 * per request, and a 401 triggers one silent refresh before the session is abandoned.
 */
export function createBreeNovaClient(options: BreeNovaClientOptions) {
  const store = options.store ?? createLocalStorageTokenStore();

  const client = createOpenApiClient<paths>({
    baseUrl: options.baseUrl,
    headers: { "Content-Type": "application/json" },
    fetch: createAuthFetch({
      baseUrl: options.baseUrl,
      store,
      onSessionLost: options.onSessionLost ?? (() => undefined),
    }),
  });

  client.use(
    idempotencyMiddleware(options.generateIdempotencyKey ?? (() => crypto.randomUUID())),
  );

  return { client, store };
}

export { describeDevice };
export type BreeNovaClient = ReturnType<typeof createBreeNovaClient>["client"];
